import React, { useState, useEffect } from "react";
import { db, functions } from "../firebase";
import { collection, query, where, getDocs, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { Card, Avatar, Spinner, Button } from "flowbite-react";
import { Calendar, Clock, Video, User, ArrowRight, Sparkles, Star, Sun } from "lucide-react";
import { NoCreditModal, ConfirmRoomEntryModal } from "./CreditModals";
import AppHeader from "./AppHeader";
import { getUserNameFromRoomId, getParticipantCount } from "../utils/roomUtils";
import { useAuth } from "./AuthContext";
import { useUserParticipations } from "../hooks/useUserParticipations";
import PublicSessionsList, { PublicAppointment } from "./PublicSessionsList";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";

interface PublicRoom {
  id: string;
  name?: string;
  createdBy: string;
  createdByName?: string;
  participantCount: number;
  createdAt: Date;
  isActive: boolean;
  public: boolean;
}

const OpenRooms: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<PublicAppointment[]>([]);
  const [rooms, setRooms] = useState<PublicRoom[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schedulingAppointment, setSchedulingAppointment] = useState<string | null>(null);
  const [schedulingError, setSchedulingError] = useState<{ [key: string]: string }>({});
  const [schedulingSuccess, setSchedulingSuccess] = useState<{ [key: string]: boolean }>({});
  const [userParticipations, addParticipation] = useUserParticipations(user?.uid);
  const [participationCredits, setParticipationCredits] = useState<number>(0);
  const [showNoCreditModal, setShowNoCreditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [isEnteringRoom, setIsEnteringRoom] = useState(false);

  useEffect(() => {
    let unsubscribeRooms: (() => void) | null = null;

    const fetchAppointments = async () => {
      try {
        const now = new Date();
        const baseFilter = [where("dataSessao", ">=", now), where("isPublic", "==", true), where("status", "==", "confirmado")];
        const appointmentsQuery1 = query(collection(db, "appointments"), where("isPublic", "==", true), ...baseFilter);

        const appointmentsQuery2 = query(collection(db, "appointments"), where("permiteParticipantes", "==", true), ...baseFilter);

        const [appointmentsSnapshot1, appointmentsSnapshot2] = await Promise.all([
          getDocs(appointmentsQuery1),
          getDocs(appointmentsQuery2),
        ]);

        const appointmentsMap = new Map();

        const appointmentPromises = [...appointmentsSnapshot1.docs, ...appointmentsSnapshot2.docs].map(async (doc) => {
          if (!appointmentsMap.has(doc.id)) {
            const data = doc.data();
            let scheduleDate = data.dataSessao.toDate();
            if (!scheduleDate) {
              scheduleDate = new Date(data.dataAgendamento);
              const scheduleTime = data.horarioAgendamento?.split(":") || ["00", "00"];
              scheduleDate.setHours(parseInt(scheduleTime[0], 10));
              scheduleDate.setMinutes(parseInt(scheduleTime[1], 10));
            }

            let participantCount = data.participantCount;
            if (typeof participantCount !== "number") {
              try {
                const participantsSnap = await getDocs(collection(db, "appointments", doc.id, "participants"));
                participantCount = participantsSnap.size;
              } catch (error) {
                console.error(`Erro ao contar participantes para ${doc.id}:`, error);
                participantCount = 0;
              }
            }

            appointmentsMap.set(doc.id, {
              id: doc.id,
              therapistName: data.terapeutaNomePublico || data.terapeutaNome || "",
              therapistId: data.terapeutaId,
              patientName: data.pacienteNome,
              patientId: data.pacienteId,
              description: data.description,
              date: scheduleDate,
              duration: data.sessionDuration || 60,
              status: data.status === "confirmado" ? ("scheduled" as const) : (data.status as "scheduled" | "in-progress" | "completed"),
              roomId: data.roomId,
              public: data.isPublic || data.permiteParticipantes,
              expectedParticipants: participantCount,
            });
          }
        });

        await Promise.all(appointmentPromises);

        const appointmentsList = Array.from(appointmentsMap.values());
        setAppointments(appointmentsList);
      } catch (err: any) {
        console.error("Erro ao buscar appointments:", err);
        setError(err?.message || "Erro ao carregar appointments.");
      }
    };

    const setupRoomsSnapshot = () => {
      const roomsQuery = query(collection(db, "rooms"), where("isPublic", "==", true), where("isActive", "==", true));

      unsubscribeRooms = onSnapshot(
        roomsQuery,
        async (snapshot) => {
          // Processa as rooms e busca os nomes dos usuários e conta participantes
          const roomsPromises = snapshot.docs.map(async (docSnapshot) => {
            const data = docSnapshot.data();

            // Busca o nome do usuário baseado no ID da sala
            let createdByName = data.createdByName;
            if (!data.createdByName) {
              createdByName = await getUserNameFromRoomId(docSnapshot.id);
            }

            // Conta os participantes reais da sala
            const participantCount = await getParticipantCount(docSnapshot.id);

            return {
              id: docSnapshot.id,
              name: data.name,
              createdBy: data.createdBy,
              createdByName,
              participantCount,
              createdAt: data.sessionStartedAt?.toDate() || new Date(),
              isActive: data.isActive,
              public: data.public,
            } as PublicRoom;
          });

          const allRooms = await Promise.all(roomsPromises);
          // Filtra apenas salas com participantes ativos
          const roomsList = allRooms.filter((room) => room.participantCount > 0);
          setRooms(roomsList);
          console.log("Salas ao vivo atualizadas em tempo real:", roomsList.length, "de", allRooms.length, "total");
        },
        (err) => {
          console.error("Erro no snapshot das salas:", err);
          setError(err?.message || "Erro ao monitorar salas ao vivo.");
        }
      );
    };

    const initializeData = async () => {
      setIsLoading(true);
      try {
        await fetchAppointments();
        setupRoomsSnapshot();
      } catch (err: any) {
        console.error("Erro ao inicializar dados:", err);
        setError(err?.message || "Erro ao carregar dados.");
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();

    // Cleanup function
    return () => {
      if (unsubscribeRooms) {
        unsubscribeRooms();
      }
    };
  }, []);

  // Buscar créditos de participação do usuário
  useEffect(() => {
    if (!user?.uid) {
      setParticipationCredits(0);
      return;
    }

    const fetchParticipationCredits = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const credits = userData?.creditBalances?.sessions?.participation || 0;
          setParticipationCredits(credits);
        }
      } catch (error) {
        console.error("Erro ao buscar créditos de participação:", error);
      }
    };

    fetchParticipationCredits();
  }, [user?.uid]);

  const handleEnterRoom = (roomId: string) => {
    if (participationCredits > 0) {
      setSelectedRoomId(roomId);
      setShowConfirmModal(true);
    } else {
      setShowNoCreditModal(true);
    }
  };

  const confirmEnterRoom = async () => {
    if (!selectedRoomId || !user?.uid) return;

    setIsEnteringRoom(true);
    
    try {
      // Debita o crédito de participação via Firebase Function
      const consumeCredit = httpsCallable(functions, "consumeParticipationCreditForRoom");
      const result = await consumeCredit({ roomId: selectedRoomId });

      // Atualiza o saldo local de créditos apenas se não for o criador
      const isCreator = (result.data as any)?.isCreator;
      if (!isCreator) {
        setParticipationCredits(prev => Math.max(0, prev - 1));
      }

      // Fecha o modal e redireciona para a sala
      setShowConfirmModal(false);
      navigate(`/sala/${selectedRoomId}`);
    } catch (error: any) {
      console.error("Erro ao entrar na sala:", error);
      setShowConfirmModal(false);
      
      // Mostra mensagem de erro amigável
      const errorMessage = error.message || "Erro ao processar entrada na sala. Tente novamente.";
      alert(errorMessage);
    } finally {
      setIsEnteringRoom(false);
      setSelectedRoomId(null);
    }
  };

  const handleScheduleParticipation = async (appointmentId: string) => {
    if (!user?.uid) {
      setSchedulingError((prev) => ({ ...prev, [appointmentId]: "Você precisa estar logado para agendar participação." }));
      return;
    }

    if (userParticipations.has(appointmentId)) {
      setSchedulingError((prev) => ({ ...prev, [appointmentId]: "Você já está registrado nesta sessão." }));
      return;
    }

    setSchedulingAppointment(appointmentId);
    setSchedulingError((prev) => ({ ...prev, [appointmentId]: "" }));
    setSchedulingSuccess((prev) => ({ ...prev, [appointmentId]: false }));

    try {
      const consumeTicket = httpsCallable(functions, "consumeRepresentativeTicket");
      await consumeTicket({ appointmentId });

      setSchedulingSuccess((prev) => ({ ...prev, [appointmentId]: true }));
      addParticipation(appointmentId);

      setTimeout(() => {
        setSchedulingSuccess((prev) => ({ ...prev, [appointmentId]: false }));
      }, 3000);
    } catch (error: any) {
      console.error("Erro ao agendar participação:", error);
      const errorMessage = error.message || "Erro ao agendar participação. Tente novamente.";
      setSchedulingError((prev) => ({ ...prev, [appointmentId]: errorMessage }));
    } finally {
      setSchedulingAppointment(null);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  return (
    <>
      <AppHeader />

      <div className="container bg-gradient-to-br from-[#F3F6F1] via-[#F8FAF7] to-[#F0F4ED] mx-auto max-w-6xl px-4 pt-24 pb-8 sm:pt-28 sm:pb-12 relative overflow-hidden">
        <div className="absolute top-10 left-10 w-20 h-20 bg-[#A56096]/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-32 right-16 w-16 h-16 bg-[#3A5859]/10 rounded-full blur-lg animate-bounce"></div>
        <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-[#A56096]/15 rounded-full blur-md animate-pulse delay-1000"></div>
        <div className="absolute bottom-32 right-1/3 w-24 h-24 bg-[#3A5859]/8 rounded-full blur-xl animate-bounce delay-500"></div>

        <div className="absolute top-20 left-1/3">
          <Sparkles className="w-6 h-6 text-[#A56096]/30 animate-pulse" />
        </div>
        <div className="absolute top-40 right-1/4">
          <Star className="w-5 h-5 text-[#3A5859]/40 animate-bounce delay-300" />
        </div>

        <div className="absolute top-1/3 right-12">
          <Sun className="w-7 h-7 text-[#3A5859]/25 animate-spin" style={{ animationDuration: "8s" }} />
        </div>

        <div className="text-center mb-12 relative z-10">
          <h2 className="text-3xl font-bold tracking-tight text-[#3A5859] sm:text-4xl mb-6">Salas Abertas</h2>
          <p className="text-lg text-[#496566] max-w-2xl mx-auto leading-relaxed">
            Participe como observador ou representante de uma sessão e contribua para o bem-estar de outras pessoas.
          </p>
        </div>

        {isLoading && (
          <div className="flex flex-col items-center justify-center py-16 min-h-[400px] relative z-10">
            <Spinner aria-label="Carregando salas abertas..." size="xl" />
            <p className="mt-4 text-gray-600 text-lg">Carregando...</p>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-16 min-h-[400px] relative z-10">
            <p className="text-red-600 text-lg">{error}</p>
          </div>
        )}

        {!isLoading && !error && (
          <>
            <div className="relative">
              <div className="flex items-center justify-center mb-8">
                <div className="flex items-center bg-white/70 backdrop-blur-sm rounded-full px-6 py-3 shadow-lg border border-white/20">
                  <div className="p-2 bg-gradient-to-r from-[#A56096] to-[#3A5859] rounded-full mr-3">
                    <Video className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#3A5859]">Sessões ao vivo</h3>
                  <Sparkles className="w-5 h-5 ml-3 text-[#A56096] animate-pulse" />
                </div>
              </div>

              {rooms.length === 0 ? (
                <Card className="p-8 text-center bg-white/60 backdrop-blur-sm border-dashed border-2 border-[#A56096]/30">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="p-4 bg-gradient-to-br from-[#A56096]/20 to-[#3A5859]/20 rounded-full">
                      <Video className="w-8 h-8 text-[#A56096]" />
                    </div>
                    <p className="text-gray-500">Nenhuma sessão ao vivo disponível no momento.</p>
                    <div className="flex items-center space-x-2">
                      <Star className="w-4 h-4 text-[#A56096]/60 animate-pulse" />
                      <span className="text-sm text-gray-400">Em breve novas sessões estarão disponíveis</span>
                      <Star className="w-4 h-4 text-[#A56096]/60 animate-pulse delay-500" />
                    </div>
                  </div>
                </Card>
              ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {rooms.map((room) => (
                    <Card
                      key={room.id}
                      className="hover:shadow-xl hover:scale-105 transition-all duration-300 bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:bg-white/90">
                      <div className="p-6 relative overflow-hidden">
                        <div className="absolute top-2 right-2">
                          <Sparkles className="w-4 h-4 text-[#A56096]/30 animate-pulse" />
                        </div>
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3 min-w-0 flex-1">
                            <Avatar
                              placeholderInitials={
                                room.createdByName
                                  ?.split(" ")
                                  .map((n) => n[0])
                                  .join("")
                                  .substring(0, 2) || "R"
                              }
                              rounded
                              size="sm"
                              className="flex-shrink-0"
                            />
                            <div className="min-w-0 flex-1">
                              <h4 className="font-semibold text-[#3A5859] truncate">{room.name || room.createdByName}</h4>
                            </div>
                          </div>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 flex-shrink-0 ml-2">
                            Ativa
                          </span>
                        </div>

                        <div className="space-y-2 mb-4">
                          <div className="flex items-center text-sm text-gray-600">
                            <User className="w-4 h-4 mr-2" />
                            {room.participantCount} participante{room.participantCount !== 1 ? "s" : ""}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <Clock className="w-4 h-4 mr-2" />
                            Iniciada em: {formatDate(room.createdAt)}
                          </div>
                        </div>

                        <Button 
                          className="w-full bg-gradient-to-r from-[#A56096] to-[#8B4A7A] hover:from-[#8B4A7A] hover:to-[#7A4169] transform hover:scale-[1.02] transition-all duration-200 shadow-lg"
                          onClick={() => handleEnterRoom(room.id)}
                          disabled={isEnteringRoom && selectedRoomId === room.id}
                        >
                          {isEnteringRoom && selectedRoomId === room.id ? (
                            <>
                              <Spinner size="sm" className="mr-2" />
                              Entrando...
                            </>
                          ) : (
                            <>
                              <Video className="w-4 h-4 mr-2" />
                              Entrar na Sala
                              <ArrowRight className="w-4 h-4 ml-2" />
                              <Sparkles className="w-3 h-3 ml-1 animate-pulse" />
                            </>
                          )}
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-16 relative">
              <div className="flex items-center justify-center mb-8">
                <div className="flex items-center bg-white/70 backdrop-blur-sm rounded-full px-6 py-3 shadow-lg border border-white/20">
                  <div className="p-2 bg-gradient-to-r from-[#3A5859] to-[#A56096] rounded-full mr-3">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#3A5859]">Sessões futuras</h3>
                  <Star className="w-5 h-5 ml-3 text-[#A56096] animate-pulse" />
                </div>
              </div>

              <PublicSessionsList
                appointments={appointments}
                isLoading={false}
                userParticipations={userParticipations}
                schedulingAppointment={schedulingAppointment}
                schedulingError={schedulingError}
                schedulingSuccess={schedulingSuccess}
                onScheduleParticipation={handleScheduleParticipation}
                emptyMessage="Nenhuma sessão futura disponível no momento."
                currentUserId={user?.uid}
                participationCredits={participationCredits}
                onViewPlans={() => navigate("/planos?secao=participante")}
              />
            </div>
          </>
        )}

        <NoCreditModal
          isOpen={showNoCreditModal}
          onClose={() => setShowNoCreditModal(false)}
          onViewPlans={() => navigate("/planos?secao=participante")}
          type="room"
        />

        <ConfirmRoomEntryModal
          isOpen={showConfirmModal}
          onClose={() => {
            setShowConfirmModal(false);
            setSelectedRoomId(null);
          }}
          onConfirm={confirmEnterRoom}
          isProcessing={isEnteringRoom}
          participationCredits={participationCredits}
        />
      </div>
    </>
  );
};

export default OpenRooms;
